import { computed, DestroyRef, effect, inject, Injectable, signal, untracked, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { LayerSpecification, MapGeoJSONFeature, MapMouseEvent } from 'maplibre-gl';
import { filter, firstValueFrom } from 'rxjs';

import { LayerListBloc } from '@connect-services/client/blocs/layer-list.bloc';
import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { fieldInterventionsBaseRouteRegex } from '@connect-services/client/guards/can-navigate.guard';
import {
    BroadcastChannelMessageEnum,
    BroadcastChannelNameEnum,
    BroadcastChannelSourceEnum,
    SelectedEntitiesByLayerIdBroadcastChannelData,
} from '@connect-services/client/models/broadcast-channel-data.model';
import { LayerStyleTypeEnum } from '@connect-services/client/models/highlight-entity.model';
import { AssertService, GeometryChildrenTypes } from '@connect-services/client/services/assert.service';
import { ContentLayoutService } from '@connect-services/client/services/content-layout.service';
import { CreateFieldInterventionService } from '@connect-services/client/services/create-field-intervention.service';
import { EditEntityDetailService } from '@connect-services/client/services/edit-entity-detail.service';
import { ModeUiSource } from '@connect-services/client/services/edit-mode-layer-panel.service';
import { EntitiesTableByLayerService } from '@connect-services/client/services/entities-table-by-layer.service';
import { FetchAllService } from '@connect-services/client/services/fetch-all.service';
import { FieldInterventionChangeSelectionService } from '@connect-services/client/services/field-intervention-change-selection.service';
import { FieldInterventionZoneSelectionService } from '@connect-services/client/services/field-intervention-zone-selection.service';
import { GeographicFilterService } from '@connect-services/client/services/geographic-filter.service';
import { GeometryEditPreviewService } from '@connect-services/client/services/geometry-edit-preview.service';
import { HomeService } from '@connect-services/client/services/home.service';
import { LayerService } from '@connect-services/client/services/layer.service';
import { MapSelectionHighlightService } from '@connect-services/client/services/map/selection/map-selection-highlight.service';
import {
    SelectedEntitiesByLayerId,
    SelectedEntity,
    SelectionStateService,
} from '@connect-services/client/services/map/selection/selection-state.service';
import { MapCreationGeomService } from '@connect-services/client/services/map-creation-geom.service';
import { MapDrawService } from '@connect-services/client/services/map-draw.service';
import { MapEditGeomService } from '@connect-services/client/services/map-edit-geom.service';
import { TARGET_CURSOR } from '@connect-services/client/services/map-edit-mode.service';
import { MapLayerService } from '@connect-services/client/services/map-layer.service';
import { MapModal, MapModalService } from '@connect-services/client/services/map-modal.service';
import { MapGeoJSONFeatureWithLayer, MapPopupService } from '@connect-services/client/services/map-popup.service';
import { SelectZoneLayerService } from '@connect-services/client/services/select-zone-layer.service';
import { SourcesService } from '@connect-services/client/services/sources.service';
import { LayersAndEntitiesSelection, StatisticService } from '@connect-services/client/services/statistic.service';
import { ZoneDelimiterService } from '@connect-services/client/services/zone-delimiter.service';
import { SelectOption } from '@connect-services/src/lib/components/select/select.component';
import {
    Geometry,
    Layer,
    LayerControllerGetEntities200ResponseAllOfItemsInner,
    LayerControllerGetEntitiesRequestParams,
    LayerGeom,
    LayersService,
    LineString,
    MultiLineString,
    MultiPoint,
    MultiPolygon,
    Point,
    Polygon,
} from '@connect-services/src/sdk/api';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

export enum MapModalSelectionModeEnum {
    FIELD_INTERVENTION = 'field_intervention',
    FIELD_INTERVENTION_AREA = 'field_intervention_area',
    SELECTION = 'selection',
    STATISTICS = 'statistics',
}

export enum FilterModeEnum {
    DRAW = 'draw',
    SELECT_ZONE = 'select_zone',
}

export type SelectionParams = {
    filterIds?: string[];
    selectedEntities?: string[];
    selectedLayers?: string[];
    zoneDelimiter?: string;
};

interface FieldInterventionMapSelectionHandler {
    isEntitySelected(layerId: string, entityId: string): boolean;
    toggleEntitiesFromMapFeatures(features: MapGeoJSONFeatureWithLayer[]): void;
}

export const SESSION_STORAGE_SELECTION_ACTIVE_KEY = 'isSelectionActive';
export const SESSION_STORAGE_SELECTED_ENTITIES_BY_LAYER_ID_KEY = 'selectedEntitiesByLayerId';

@Injectable({
    providedIn: 'root',
})
export class SelectionService {
    public readonly isCreationActive = signal(false);
    public readonly isEditActive = signal(false);
    public readonly isEntitySelectionEnabled = computed<boolean>(
        () =>
            this.isSelectionActive() ||
            this.createFieldInterventionService.isCreateFieldIntervention() ||
            this.entitiesTableByLayerService.isPlanningSelectionActive(),
    );
    public readonly isFieldInterventionAreaMode = computed(() => {
        return this.modalMode() === MapModalSelectionModeEnum.FIELD_INTERVENTION_AREA;
    });
    public readonly isFieldInterventionSelectionMode = computed(() => {
        return this.modalMode() === MapModalSelectionModeEnum.FIELD_INTERVENTION;
    });
    public readonly isFilterModalOpen = signal(false);
    public readonly isSelectionActive = signal(false);
    public readonly layersOptions = computed((): SelectOption<string[]>[] => {
        const options: SelectOption<string[]>[] = this.buildLayerOptionsFromLayersGrouped(
            this.layerService.layersGrouped(),
        );

        const layerIdsWithGeom: Set<string> = this.getLayerIdsWithGeom(this.layerListBloc.value()?.items ?? []);

        return this.filterOptionsByAllowedLayerIds(options, layerIdsWithGeom);
    });
    public readonly layersSelectedDefaultOptions = computed<SelectOption<string[]>[]>(
        () => {
            const selectedLayerIds = new Set(Object.keys(this.selectionStateService.selectedEntitiesByLayerId()));
            const availableOptions: SelectOption<string[]>[] = this.selectionLayerOptions();

            return this.filterOptionsBySelectedLayerIds(availableOptions, selectedLayerIds);
        },
        {
            equal: (previous: SelectOption<string[]>[], current: SelectOption<string[]>[]) =>
                this.areSelectOptionArraysEqual(previous, current),
        },
    );
    public readonly lineStringLayerOptions = computed((): SelectOption<string[]>[] =>
        this.buildLayerOptionsFromLayersGrouped(this.layerService.lineStringLayersGrouped()),
    );
    public readonly mapClearSelectedLayer = signal(0);
    public readonly polygonLayerOptions = computed((): SelectOption<string[]>[] =>
        this.buildLayerOptionsFromLayersGrouped(this.layerService.polygonLayersGrouped()),
    );
    public readonly selectedLayerIds = signal<string[]>([]);
    public readonly selectionLayerOptions = computed<SelectOption<string[]>[]>(() => {
        const referenceDataLayerIds: Set<string> = this.getReferenceDataLayerIds(this.layerListBloc.value()?.items);

        return this.filterOptionsWithoutReferenceData(this.layersOptions(), referenceDataLayerIds);
    });
    public readonly selectionMode: WritableSignal<FilterModeEnum | null> = signal(null);

    // eslint-disable-next-line @typescript-eslint/member-ordering
    private readonly contentLayoutService = inject(ContentLayoutService);
    private readonly createFieldInterventionService = inject(CreateFieldInterventionService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly editEntityDetailService = inject(EditEntityDetailService);
    private readonly entitiesTableByLayerService = inject(EntitiesTableByLayerService);
    private readonly fetchAllService = inject(FetchAllService);
    private readonly fieldInterventionChangeSelectionService = inject(FieldInterventionChangeSelectionService);
    private readonly fieldInterventionsBaseRouteRegex = new RegExp(fieldInterventionsBaseRouteRegex);
    private readonly fieldInterventionZoneSelectionService = inject(FieldInterventionZoneSelectionService);
    private readonly geographicFilterService = inject(GeographicFilterService);
    private readonly geometryEditPreviewService = inject(GeometryEditPreviewService);
    private readonly homeService = inject(HomeService);
    private readonly layerBroadcastChannel = new BroadcastChannel(BroadcastChannelNameEnum.LAYERS);
    private readonly layerListBloc = inject(LayerListBloc);
    private readonly layerService = inject(LayerService);
    private readonly mapCreationGeomService = inject(MapCreationGeomService);
    private readonly mapDrawService = inject(MapDrawService);
    private readonly mapEditGeomService = inject(MapEditGeomService);
    private readonly mapLayerService = inject(MapLayerService);
    private readonly mapModalService = inject(MapModalService);
    private readonly mapPopupService = inject(MapPopupService);
    private readonly mapSelectionHighlightService = inject(MapSelectionHighlightService);
    private readonly modalMode: WritableSignal<MapModalSelectionModeEnum | null> = signal(null);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly router = inject(Router);
    private readonly sdkLayersService = inject(LayersService);
    private readonly selectionStateService = inject(SelectionStateService);
    private readonly selectZoneLayerService = inject(SelectZoneLayerService);
    private readonly snackBarService = inject(SnackBarService);
    private readonly sourcesService = inject(SourcesService);
    private readonly statisticService = inject(StatisticService);
    private readonly zoneDelimiterService = inject(ZoneDelimiterService);

    public constructor() {
        this.initModalFieldInterventionRoute();
        this.restoreIsSelectionActiveFromSessionStorage();
        this.listenSelectionActiveBroadcast();

        effect(() => {
            const selectedEntitiesByLayerId = this.selectionStateService.selectedEntitiesByLayerId();

            if (!this.homeService.isExternalPage() && this.contentLayoutService.isDataTableView()) {
                return;
            }
            untracked(async () => {
                await this.mapSelectionHighlightService.synchronizeHighlights(selectedEntitiesByLayerId);
                this.refreshSelectionHighlightsForVisibleLayers();
            });
        });

        effect(() => {
            this.synchronizeToSessionStorage(this.isSelectionActive());
        });

        effect(() => {
            const isSelectionActive = this.isSelectionActive();
            const isCreationActive = this.isCreationActive();
            const map = this.mapCreationGeomService.map;

            if (!map) {
                return;
            }

            if (isSelectionActive) {
                map.getCanvas().style.cursor = 'grab';

                return;
            }

            if (isCreationActive) {
                map.getCanvas().style.cursor = TARGET_CURSOR;
            }
        });
    }

    public async apply(): Promise<void> {
        switch (this.modalMode()) {
            case MapModalSelectionModeEnum.STATISTICS:
                this.applyStatisticsFilters();
                break;
            case MapModalSelectionModeEnum.FIELD_INTERVENTION:
                if (
                    this.createFieldInterventionService.isCreateFieldIntervention() ||
                    this.fieldInterventionChangeSelectionService.isActive()
                ) {
                    this.createFieldInterventionService.resetPagination();
                } else {
                    this.entitiesTableByLayerService.resetPagination();
                }

                await this.applyFieldInterventionFilters();
                break;
            case MapModalSelectionModeEnum.FIELD_INTERVENTION_AREA:
                this.createFieldInterventionService.confirmFieldInterventionAreaSelection();
                break;
            case MapModalSelectionModeEnum.SELECTION:
                await this.applySelection();
                break;
        }
    }

    public buildLayerEntitiesRequestParams(
        layerId: string,
        selectionParams?: SelectionParams,
    ): LayerControllerGetEntitiesRequestParams {
        const hasGeom = !!selectionParams?.zoneDelimiter || !!selectionParams?.selectedLayers?.length;

        return {
            id: layerId,
            ...(selectionParams?.filterIds && {
                filterIds: selectionParams.filterIds,
            }),
            ...(selectionParams?.selectedEntities && {
                selectedEntities: selectionParams.selectedEntities,
            }),
            ...(selectionParams?.selectedLayers && {
                selectedLayers: selectionParams.selectedLayers,
            }),
            ...(hasGeom && {
                hasGeom: true,
            }),
            ...(selectionParams?.zoneDelimiter && {
                zonesDelimiter: [selectionParams.zoneDelimiter],
            }),
        };
    }

    public async clearAllSelections(closeModal?: boolean, source?: ModeUiSource): Promise<void> {
        const hasDeletedSelectedPolygon: boolean = this.mapDrawService.deleteSelectedPolygon();

        if (hasDeletedSelectedPolygon) {
            return;
        }

        this.mapSelectionHighlightService.clearSourcesHighlight(this.selectedLayerIds());
        this.mapSelectionHighlightService.clearFieldInterventionHighlight();

        this.selectedLayerIds.set([]);

        if (source === ModeUiSource.MAP) {
            this.mapClearSelectedLayer.update((value) => value + 1);
        }

        this.selectionStateService.clearEntitiesAndLayer();
        this.mapDrawService.resetDraw();

        switch (this.modalMode()) {
            case MapModalSelectionModeEnum.STATISTICS:
                await this.clearStatisticSelections(closeModal);

                break;

            case MapModalSelectionModeEnum.FIELD_INTERVENTION:
                if (
                    this.createFieldInterventionService.isCreateFieldIntervention() ||
                    this.fieldInterventionChangeSelectionService.isActive()
                ) {
                    this.createFieldInterventionService.resetPagination();
                    this.createFieldInterventionService.zoneDelimiter.set(undefined);
                    this.createFieldInterventionService.isZoneDelimiterFilterActive.set(false);
                    await this.createFieldInterventionService.refreshTable();

                    break;
                }

                this.entitiesTableByLayerService.resetPagination();
                this.entitiesTableByLayerService.zoneDelimiter.set(undefined);
                this.entitiesTableByLayerService.isZoneDelimiterFilterActive.set(false);
                await this.entitiesTableByLayerService.resetTableData();

                break;

            case MapModalSelectionModeEnum.SELECTION:
                await this.clearSelection(closeModal);

                break;

            default:
                break;
        }
    }

    public async fetchSelectedEntitiesForLayer(
        layerId: string,
        selectionParams?: SelectionParams,
        report?: (delta: number) => void,
    ): Promise<SelectedEntity[]> {
        const requestParams: LayerControllerGetEntitiesRequestParams = this.buildLayerEntitiesRequestParams(
            layerId,
            selectionParams,
        );

        const entities = await this.fetchAllService.getAll<
            LayerControllerGetEntitiesRequestParams,
            LayerControllerGetEntities200ResponseAllOfItemsInner
        >(this.sdkLayersService.layerControllerGetEntities.bind(this.sdkLayersService), requestParams, report);

        return entities.map((entity: LayerControllerGetEntities200ResponseAllOfItemsInner): SelectedEntity =>
            this.mapLayerEntityToSelectedEntity(entity),
        );
    }

    public isFieldInterventionSelectionEnabled(): boolean {
        return (
            this.createFieldInterventionService.isCreateFieldIntervention() ||
            this.fieldInterventionChangeSelectionService.isActive() ||
            this.entitiesTableByLayerService.isPlanningSelectionActive()
        );
    }

    public async manageSelectionFromClick(
        features: MapGeoJSONFeatureWithLayer[],
        mapClickEvent: MapMouseEvent,
    ): Promise<void> {
        if (features.length === 1) {
            this.toggleFeatureSelection(features[0]);

            return;
        }

        const alreadySelectedEntityIds: string[] = this.resolveAlreadySelectedEntityIds(features);
        const confirmedEntityIds: Set<string> = new Set(
            await this.mapPopupService.openSelectionPopup(features, mapClickEvent.lngLat, alreadySelectedEntityIds),
        );

        const selectionFeatures: MapGeoJSONFeatureWithLayer[] = features.filter((feature: MapGeoJSONFeatureWithLayer) =>
            this.hasFeatureSelectionChanged(feature, confirmedEntityIds),
        );

        if (this.isFieldInterventionSelectionEnabled()) {
            this.resolveFieldInterventionListService().toggleEntitiesFromMapFeatures(selectionFeatures);

            return;
        }

        selectionFeatures.forEach((feature: MapGeoJSONFeatureWithLayer) => this.toggleFeatureSelection(feature));
    }

    public mapSdkGeometryToStyleType(geometry: LayerGeom): LayerStyleTypeEnum {
        switch (geometry.type) {
            case Point.TypeEnum.POINT:
            case MultiPoint.TypeEnum.MULTI_POINT:
                return LayerStyleTypeEnum.SYMBOL;

            case LineString.TypeEnum.LINE_STRING:
            case MultiLineString.TypeEnum.MULTI_LINE_STRING:
                return LayerStyleTypeEnum.LINE;

            case Polygon.TypeEnum.POLYGON:
            case MultiPolygon.TypeEnum.MULTI_POLYGON:
                return LayerStyleTypeEnum.FILL;

            default:
                return LayerStyleTypeEnum.SYMBOL;
        }
    }

    public refreshSelectionHighlightsForVisibleLayers(): void {
        const mapLayerIds: string[] | null = this.getVisibleLayerIds();

        if (!mapLayerIds) {
            return;
        }

        const renderedFeatures: MapGeoJSONFeature[] = this.mapDrawService.map!.queryRenderedFeatures(undefined, {
            layers: mapLayerIds,
        });

        const selectedEntityIdsByLayerId: Map<string, Set<string>> = this.buildSelectedEntityIdsByLayerId(
            this.selectionStateService.selectedEntitiesByLayerId(),
        );

        const visibleSelectedFeatures: MapGeoJSONFeature[] = renderedFeatures.filter((feature: MapGeoJSONFeature) => {
            const layerId = feature.source;
            const uuid = feature.properties?.['uuid'];

            if (typeof uuid !== 'string') {
                return false;
            }

            return selectedEntityIdsByLayerId.get(layerId)?.has(uuid) ?? false;
        });

        const visibleEntitiesByLayerId: Record<string, SelectedEntity[]> =
            this.buildSelectedEntitiesByLayerId(visibleSelectedFeatures);

        this.mergeVisibleEntitiesIntoSelection(visibleEntitiesByLayerId);
    }

    public resetSelectionState(): void {
        this.isSelectionActive.set(false);
        this.isFilterModalOpen.set(false);
        this.setModalMode(MapModalSelectionModeEnum.SELECTION);
        this.selectionMode.set(null);
        this.selectedLayerIds.set([]);
    }

    public resolveZoneDelimiter(): SelectionParams | undefined {
        const drawnPolygonDelimiter: string | undefined = this.zoneDelimiterService.getZoneDelimiterFromDrawPolygons();

        if (drawnPolygonDelimiter) {
            return {
                zoneDelimiter: drawnPolygonDelimiter,
            };
        }

        const extentLayerAndEntities: LayersAndEntitiesSelection | undefined =
            this.geographicFilterService.selectedExtentLayersAndEntities();

        if (!extentLayerAndEntities?.layers.length) {
            return {
                selectedEntities: Object.values(this.selectionStateService.selectedEntitiesByLayerId())
                    .flat()
                    .map((entity: SelectedEntity) => entity.uuid),
                selectedLayers: Object.keys(this.selectionStateService.selectedEntitiesByLayerId()),
            };
        }

        return {
            selectedEntities: extentLayerAndEntities.entities ?? [],
            selectedLayers: extentLayerAndEntities.layers,
        };
    }

    public setModalMode(context: MapModalSelectionModeEnum): void {
        this.modalMode.set(context);
    }

    public toggleDrawPolygon(): void {
        const isCurrentlyDrawing = this.selectionMode() === FilterModeEnum.DRAW;
        const nextIsDrawing = !isCurrentlyDrawing;

        this.selectionMode.set(nextIsDrawing ? FilterModeEnum.DRAW : null);
        this.geographicFilterService.isSelectZoneActive.set(false);
        this.mapDrawService.togglePolygonDraw(nextIsDrawing);

        if (!nextIsDrawing) {
            this.selectionStateService.clearEntities();
            this.mapDrawService.stopDraw();
            this.mapDrawService.deleteAllDraw();

            return;
        }

        this.selectedLayerIds.set([]);

        this.mapDrawService.deleteCurrentDrawPolygon();
    }

    private async applyFieldInterventionFilters(): Promise<void> {
        const zoneDelimiter = this.getFieldInterventionZoneDelimiter();

        if (
            this.createFieldInterventionService.isCreateFieldIntervention() ||
            this.fieldInterventionChangeSelectionService.isActive()
        ) {
            this.createFieldInterventionService.zoneDelimiter.set(zoneDelimiter);
            this.createFieldInterventionService.isZoneDelimiterFilterActive.set(!!zoneDelimiter);
            await this.createFieldInterventionService.refreshTable();

            return;
        }

        this.entitiesTableByLayerService.zoneDelimiter.set(zoneDelimiter);
        this.entitiesTableByLayerService.isZoneDelimiterFilterActive.set(!!zoneDelimiter);
        await this.entitiesTableByLayerService.resetTableData();
    }

    private async applySelection(): Promise<void> {
        const selectedLayerIds: string[] = Object.keys(this.selectionStateService.selectedEntitiesByLayerId());
        const selectionParams: SelectionParams | undefined = this.resolveZoneDelimiter();

        if (!selectionParams) {
            return;
        }

        const selectedEntitiesByLayerId: Record<string, SelectedEntity[]> = await this.fetchSelectedEntitiesByLayerId(
            selectedLayerIds,
            selectionParams,
        );

        if (!this.isEditActive() && !this.isCreationActive()) {
            this.selectionStateService.storeSelectedEntities(selectedEntitiesByLayerId);

            this.notifyIfInvisibleSelectedLayers();
            this.refreshSelectionHighlightsForVisibleLayers();

            return;
        }

        await this.mergeGeometries(selectedEntitiesByLayerId);
    }

    private applyStatisticsFilters(): void {
        if (this.selectionMode() === FilterModeEnum.SELECT_ZONE) {
            this.applyStatisticsSelectedEntitiesByZone();

            return;
        }

        const zoneDelimiter: string | undefined = this.zoneDelimiterService.getZoneDelimiter();

        if (!zoneDelimiter) {
            return;
        }
        this.statisticService.wktStringMultipolygon.set(zoneDelimiter);
        this.geographicFilterService.isStatisticFilterApplied.set(true);
    }

    private applyStatisticsSelectedEntitiesByZone(): void {
        const selectedEntitiesUuidsBySourceIds = this.selectZoneLayerService.selectedEntityUuidsBySourceId();
        if (!selectedEntitiesUuidsBySourceIds) {
            this.statisticService.selectedLayersAndEntities.set({
                entities: [],
                layers: [],
            });

            return;
        }

        this.statisticService.selectedLayersAndEntities.set({
            entities: Object.values(selectedEntitiesUuidsBySourceIds).flat(),
            layers: Object.keys(selectedEntitiesUuidsBySourceIds),
        });

        if (!this.statisticService.isFullLayerSelected()) {
            return;
        }

        this.snackBarService.openWarningSnackBar('noEntitySelectedFilterWholeLayer');
    }

    private areSelectOptionArraysEqual(previous: SelectOption<string[]>[], current: SelectOption<string[]>[]): boolean {
        if (previous.length !== current.length) {
            return false;
        }

        return previous.every((option: SelectOption<string[]>, index: number) => {
            const currentOption: SelectOption<string[]> = current[index];

            return (
                option.label === currentOption.label &&
                option.value.length === currentOption.value.length &&
                option.value.every((id: string, i: number) => id === currentOption.value[i])
            );
        });
    }

    private buildEntitiesByLayerId(
        previous: SelectedEntitiesByLayerId,
        layerId: string,
        selectedEntity: SelectedEntity,
    ): SelectedEntitiesByLayerId {
        const entitiesForLayer: SelectedEntity[] = previous[layerId] ?? [];
        const isAlreadySelected: boolean = entitiesForLayer.some(
            (entity: SelectedEntity) => entity.uuid === selectedEntity.uuid,
        );

        const nextEntitiesForLayer: SelectedEntity[] = isAlreadySelected
            ? entitiesForLayer.filter((entity: SelectedEntity) => entity.uuid !== selectedEntity.uuid)
            : [...entitiesForLayer, selectedEntity];

        const next: SelectedEntitiesByLayerId = { ...previous };

        if (nextEntitiesForLayer.length) {
            next[layerId] = nextEntitiesForLayer;
        } else {
            delete next[layerId];
        }

        return next;
    }

    private buildLayerOptionsFromLayersGrouped(layersGrouped: Record<string, string[]>): SelectOption<string[]>[] {
        return Object.entries(layersGrouped)
            .sort((a: [string, string[]], b: [string, string[]]): number => a[0].localeCompare(b[0]))
            .map(([label, layerIds]: [string, string[]]) => {
                return {
                    label,
                    value: layerIds,
                };
            });
    }

    private buildSelectedEntitiesByLayerId(features: MapGeoJSONFeature[]): Record<string, SelectedEntity[]> {
        if (!features.length) {
            return {};
        }

        const entitiesByLayerId = features.reduce(
            (accumulator: Record<string, Map<string, SelectedEntity>>, feature: MapGeoJSONFeature) => {
                const selectedEntity: SelectedEntity = this.mapFeatureToSelectedEntity(feature);
                const layerId = feature.source;
                const entitiesForLayer = accumulator[layerId] ?? new Map<string, SelectedEntity>();

                entitiesForLayer.set(selectedEntity.uuid, selectedEntity);
                accumulator[layerId] = entitiesForLayer;

                return accumulator;
            },
            {},
        );

        return Object.fromEntries(
            Object.entries(entitiesByLayerId).map(([layerId, entityMap]: [string, Map<string, SelectedEntity>]) => {
                return [layerId, Array.from(entityMap.values())];
            }),
        );
    }

    private buildSelectedEntityIdsByLayerId(
        selectedEntitiesByLayerId: SelectedEntitiesByLayerId,
    ): Map<string, Set<string>> {
        return Object.entries(selectedEntitiesByLayerId).reduce(
            (acc: Map<string, Set<string>>, [layerId, entities]: [string, SelectedEntity[]]) => {
                acc.set(
                    layerId,
                    new Set<string>(
                        entities.map((entity: SelectedEntity) => {
                            return entity.uuid;
                        }),
                    ),
                );

                return acc;
            },
            new Map<string, Set<string>>(),
        );
    }

    private async clearSelection(closeModal?: boolean): Promise<void> {
        await this.zoneDelimiterService.resetFilters();
        this.selectionMode.set(null);

        this.removeSessionStorage();

        if (!closeModal) {
            return;
        }

        this.closeModal(true);
    }

    private async clearStatisticSelections(closeModal?: boolean): Promise<void> {
        this.statisticService.resetGeographicalFilter();
        this.geographicFilterService.resetGeographicFilterData();
        await this.zoneDelimiterService.resetFilters();

        localStorage.removeItem('exportStatisticsZoneDelimiter');

        if (!closeModal) {
            return;
        }
        this.closeModal();
    }

    private closeModal(isSelectionModal?: boolean): void {
        if (!this.isFilterModalOpen() && !this.isSelectionActive()) {
            return;
        }
        this.modalMode.set(null);
        this.selectionMode.set(null);
        this.mapDrawService.resetDraw();

        if (isSelectionModal) {
            this.mapModalService.close(MapModal.SELECTION);
            this.isSelectionActive.set(false);

            return;
        }

        this.isFilterModalOpen.set(false);
        this.mapModalService.close(MapModal.FILTER);
    }

    private async fetchSelectedEntitiesByLayerId(
        layerIds: string[],
        selectionParams: SelectionParams,
    ): Promise<Record<string, SelectedEntity[]>> {
        const entries = await Promise.all(
            layerIds.map(async (layerId: string) => {
                const entities: SelectedEntity[] = await this.fetchSelectedEntitiesForLayer(layerId, selectionParams);

                return [layerId, entities];
            }),
        );

        return Object.fromEntries(entries);
    }

    private filterOptionsByAllowedLayerIds(
        options: SelectOption<string[]>[],
        allowedLayerIds: Set<string>,
    ): SelectOption<string[]>[] {
        return options.reduce<SelectOption<string[]>[]>(
            (acc: SelectOption<string[]>[], option: SelectOption<string[]>) => {
                const value: string[] = option.value.filter((layerId: string) => allowedLayerIds.has(layerId));

                if (value.length) {
                    acc.push({
                        ...option,
                        value,
                    });
                }

                return acc;
            },
            [],
        );
    }

    private filterOptionsBySelectedLayerIds(
        availableOptions: SelectOption<string[]>[],
        selectedLayerIds: ReadonlySet<string>,
    ): SelectOption<string[]>[] {
        if (!selectedLayerIds.size || !availableOptions.length) {
            return [];
        }

        return availableOptions.filter((option: SelectOption<string[]>) =>
            option.value.every((layerId: string) => selectedLayerIds.has(layerId)),
        );
    }

    private filterOptionsWithoutReferenceData(
        options: SelectOption<string[]>[],
        referenceDataLayerIds: Set<string>,
    ): SelectOption<string[]>[] {
        return options
            .map((option: SelectOption<string[]>) => {
                return {
                    ...option,
                    value: option.value.filter((layerId: string) => {
                        return !referenceDataLayerIds.has(layerId);
                    }),
                };
            })
            .filter((option: SelectOption<string[]>) => {
                return !!option.value.length;
            });
    }

    private getFieldInterventionZoneDelimiter(): string | undefined {
        return this.selectionMode() === FilterModeEnum.DRAW
            ? this.fieldInterventionZoneSelectionService.getZoneDelimiterFromDrawnPolygons()
            : this.fieldInterventionZoneSelectionService.getZoneDelimiterFromSelectedGeometries();
    }

    private getLayerIds(selectedLayerIds: string[]): string[] {
        if (!this.mapDrawService.map) {
            return [];
        }

        const selectedLayerIdSet = new Set<string>(selectedLayerIds);
        const styleLayers: LayerSpecification[] = this.mapDrawService.map.getStyle().layers ?? [];

        return styleLayers
            .filter((styleLayer: LayerSpecification) => {
                return (
                    'source' in styleLayer &&
                    selectedLayerIdSet.has(styleLayer.source) &&
                    !styleLayer.id.endsWith('-label')
                );
            })
            .map((styleLayer: LayerSpecification) => styleLayer.id);
    }

    private getLayerIdsWithGeom(layers: Layer[]): Set<string> {
        return new Set<string>(layers.filter((layer: Layer) => layer.hasGeom).map((layer: Layer) => layer.id));
    }

    private getReferenceDataLayerIds(layers: Layer[] | undefined): Set<string> {
        return new Set<string>(
            (layers ?? [])
                .filter((layer: Layer) => {
                    return layer.type === Layer.TypeEnum.REFERENCE_DATA;
                })
                .map((layer: Layer) => {
                    return layer.id;
                }),
        );
    }

    private getVisibleEntityIdsForLayer(
        visibleEntitiesByLayerId: Record<string, SelectedEntity[]>,
        layerId: string,
    ): Set<string> {
        return new Set<string>(
            (visibleEntitiesByLayerId[layerId] ?? []).map((entity: SelectedEntity) => {
                return entity.uuid;
            }),
        );
    }

    private getVisibleLayerIds(): string[] | null {
        const selectedLayerIds: string[] = Object.keys(this.selectionStateService.selectedEntitiesByLayerId());

        if (!this.mapDrawService.map || !selectedLayerIds.length) {
            return null;
        }

        const visibleLayerIds: string[] = selectedLayerIds.filter((layerId: string) => {
            return this.mapLayerService.isLayerCurrentlyVisible(layerId);
        });

        if (!visibleLayerIds.length) {
            this.markAllSelectedEntitiesAsNotVisible(selectedLayerIds);

            return null;
        }

        return this.getLayerIds(visibleLayerIds);
    }

    private async handleCreationMergedGeometries(mergedGeometry: GeometryChildrenTypes): Promise<void> {
        if (mergedGeometry.type === Polygon.TypeEnum.POLYGON) {
            this.mapCreationGeomService.currentGeometryPosition = mergedGeometry.coordinates.flat();
        }

        if (mergedGeometry.type === LineString.TypeEnum.LINE_STRING) {
            this.mapCreationGeomService.currentGeometryPosition = mergedGeometry.coordinates;
        }

        await this.mapCreationGeomService.createShape();
    }

    private handleEditMergedGeometries(layer: Layer, mergedGeometry: Geometry): void {
        const project = this.projectBloc.value();
        if (!layer.tableName || !project) {
            return;
        }

        const businessIdentifierKey = layer.businessIdentifier ?? 'uuid';
        const properties = {
            ...this.editEntityDetailService.bookmarkedAttributesForm()?.value,
            ...this.editEntityDetailService.notBookmarkedAttributesForm()?.value,
        };

        AssertService.assertGeometryType(properties['geom']);
        this.geometryEditPreviewService.set({
            after: mergedGeometry,
            before: properties['geom'],
            businessIdentifier: {
                key: businessIdentifierKey,
                value: properties?.[businessIdentifierKey] ?? this.editEntityDetailService.entityId(),
            },
            entityId: this.editEntityDetailService.entityId(),
            layerId: layer.id,
            tableName: layer.tableName,
        });

        this.redrawSource(layer.id);
    }

    private hasFeatureSelectionChanged(
        feature: MapGeoJSONFeatureWithLayer,
        confirmedEntityIds: ReadonlySet<string>,
    ): boolean {
        const entityId = this.resolveFeatureEntityId(feature);

        return this.isFeatureEntitySelected(feature) !== confirmedEntityIds.has(entityId);
    }

    private hasVisibilityChanged(entities: SelectedEntity[], visibleEntityIds: Set<string>): boolean {
        return entities.some((entity: SelectedEntity) => {
            return entity.isVisible !== visibleEntityIds.has(entity.uuid);
        });
    }

    private initModalFieldInterventionRoute(): void {
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(() => {
                const shouldBeOpen = this.fieldInterventionsBaseRouteRegex.test(this.router.url);

                if (shouldBeOpen) {
                    this.openForFieldIntervention();

                    return;
                }

                if (this.isFieldInterventionSelectionMode()) {
                    this.closeModal();
                }
            });

        queueMicrotask(() => {
            const shouldBeOpen = this.fieldInterventionsBaseRouteRegex.test(this.router.url);

            if (shouldBeOpen) {
                this.openForFieldIntervention();

                return;
            }

            this.closeModal();
        });
    }

    private isFeatureEntitySelected(feature: MapGeoJSONFeatureWithLayer): boolean {
        const entityId = this.resolveFeatureEntityId(feature);

        if (this.isFieldInterventionSelectionEnabled()) {
            return this.resolveFieldInterventionListService().isEntitySelected(feature.source, entityId);
        }

        const entitiesForLayer: SelectedEntity[] =
            this.selectionStateService.selectedEntitiesByLayerId()[feature.source] ?? [];

        return entitiesForLayer.some((entity: SelectedEntity) => entity.uuid === entityId);
    }

    private listenSelectionActiveBroadcast(): void {
        this.layerBroadcastChannel.onmessage = (
            event: MessageEvent<SelectedEntitiesByLayerIdBroadcastChannelData>,
        ): void => {
            if (
                event.data.message !== BroadcastChannelMessageEnum.SELECTION_ACTIVE ||
                typeof event.data.additionalData !== 'boolean' ||
                this.isSelectionActive() === event.data.additionalData
            ) {
                return;
            }

            this.isSelectionActive.set(event.data.additionalData);
        };
    }

    private mapEntitiesWithVisibility(entities: SelectedEntity[], visibleEntityIds: Set<string>): SelectedEntity[] {
        return entities.map((entity: SelectedEntity) => {
            return {
                ...entity,
                isVisible: visibleEntityIds.has(entity.uuid),
            };
        });
    }

    private mapFeatureToSelectedEntity(feature: MapGeoJSONFeature): SelectedEntity {
        AssertService.hasKey(feature.properties, 'uuid');
        AssertService.isString(feature.properties['uuid']);

        const uuid: string = feature.properties['uuid'];

        return {
            ...feature.properties,
            isInCreationState: false,
            isVisible: true,
            styleType: this.mapRenderedGeometryToStyleType(feature.geometry),
            uuid,
        };
    }

    private mapLayerEntityToSelectedEntity(
        entity: LayerControllerGetEntities200ResponseAllOfItemsInner,
    ): SelectedEntity {
        AssertService.hasKey(entity, 'uuid');
        AssertService.isString(entity.uuid);

        return {
            ...entity,
            isInCreationState: false,
            isVisible: false,
            styleType: this.resolveStyleTypeFromEntity(entity),
            uuid: entity.uuid,
        };
    }

    private mapRenderedGeometryToStyleType(geometry: MapGeoJSONFeature['geometry']): LayerStyleTypeEnum {
        switch (geometry.type) {
            case Point.TypeEnum.POINT:
            case MultiPoint.TypeEnum.MULTI_POINT: {
                return LayerStyleTypeEnum.SYMBOL;
            }

            case LineString.TypeEnum.LINE_STRING:
            case MultiLineString.TypeEnum.MULTI_LINE_STRING: {
                return LayerStyleTypeEnum.LINE;
            }

            case Polygon.TypeEnum.POLYGON:
            case MultiPolygon.TypeEnum.MULTI_POLYGON: {
                return LayerStyleTypeEnum.FILL;
            }

            default: {
                return LayerStyleTypeEnum.SYMBOL;
            }
        }
    }

    private markAllSelectedEntitiesAsNotVisible(selectedLayerIds: string[]): void {
        this.selectionStateService.updateSelectedEntitiesByLayerId((previous: SelectedEntitiesByLayerId) => {
            let hasChanged = false;
            const next: SelectedEntitiesByLayerId = { ...previous };

            selectedLayerIds.forEach((layerId: string) => {
                const previousForLayer: SelectedEntity[] = next[layerId] ?? [];

                next[layerId] = previousForLayer.map((entity: SelectedEntity) => {
                    if (entity.isVisible) {
                        hasChanged = true;
                    }

                    return {
                        ...entity,
                        isVisible: false,
                    };
                });
            });

            return hasChanged ? next : previous;
        });
    }

    private async mergeGeometries(selectedEntitiesByLayerId: Record<string, SelectedEntity[]>): Promise<void> {
        const entitiesByLayerId = Object.entries(selectedEntitiesByLayerId).reduce(
            (acc: Record<string, string[]>, [layerId, selectedEntities]: [string, SelectedEntity[]]) => {
                return {
                    ...acc,
                    [layerId]: selectedEntities.map((selectedEntity: SelectedEntity) => selectedEntity.uuid),
                };
            },
            {},
        );

        const result = await firstValueFrom(
            this.sdkLayersService.layerControllerMergedGeometries({
                mergedGeometriesDto: { entitiesByLayerId },
            }),
        );

        if (!result.mergedGeometry) {
            this.snackBarService.openErrorSnackBar(`editMode.mergeGeometries.${result.message}`);

            return;
        }

        AssertService.assertGeometryType(result.mergedGeometry);
        if (this.isCreationActive()) {
            await this.handleCreationMergedGeometries(result.mergedGeometry);
        }

        const layer = this.editEntityDetailService.layer();
        if (this.isEditActive() && layer?.tableName) {
            this.handleEditMergedGeometries(layer, result.mergedGeometry);
        }

        this.closeModal(true);
    }

    private mergeVisibleEntitiesIntoSelection(visibleEntitiesByLayerId: Record<string, SelectedEntity[]>): void {
        this.selectionStateService.updateSelectedEntitiesByLayerId((previous: SelectedEntitiesByLayerId) => {
            let hasChanged = false;
            const next: SelectedEntitiesByLayerId = { ...previous };

            Object.keys(next).forEach((layerId: string) => {
                const previousEntitiesForLayer: SelectedEntity[] = next[layerId] ?? [];

                if (!previousEntitiesForLayer.length) {
                    return;
                }

                const visibleEntityIds: Set<string> = this.getVisibleEntityIdsForLayer(
                    visibleEntitiesByLayerId,
                    layerId,
                );

                if (!this.hasVisibilityChanged(previousEntitiesForLayer, visibleEntityIds)) {
                    return;
                }

                hasChanged = true;

                next[layerId] = this.mapEntitiesWithVisibility(previousEntitiesForLayer, visibleEntityIds);
            });

            return hasChanged ? next : previous;
        });
    }

    private notifyIfInvisibleSelectedLayers(): void {
        const selectedLayerIds: string[] = Object.keys(this.selectionStateService.selectedEntitiesByLayerId());
        const layerIds: string[] = this.getLayerIds(selectedLayerIds);

        if (!selectedLayerIds.length || layerIds.length) {
            return;
        }

        this.snackBarService.openWarningSnackBar('selectedEntitiesNotVisibleAtCurrentZoom');
    }

    private openForFieldIntervention(): void {
        if (this.isFilterModalOpen()) {
            return;
        }

        this.isFilterModalOpen.set(true);
        this.setModalMode(MapModalSelectionModeEnum.FIELD_INTERVENTION);
        this.mapModalService.open(MapModal.FILTER);
    }

    private redrawSource(layerId: string): void {
        const source = this.sourcesService.sources().find((source) => source.sourceId === layerId);

        if (!source) {
            return;
        }

        this.mapEditGeomService.drawSource(source);
    }

    private removeSessionStorage(): void {
        sessionStorage.removeItem(SESSION_STORAGE_SELECTION_ACTIVE_KEY);
        sessionStorage.removeItem(SESSION_STORAGE_SELECTED_ENTITIES_BY_LAYER_ID_KEY);
    }

    private resolveAlreadySelectedEntityIds(features: MapGeoJSONFeatureWithLayer[]): string[] {
        return features
            .filter((feature: MapGeoJSONFeatureWithLayer) => this.isFeatureEntitySelected(feature))
            .map((feature: MapGeoJSONFeatureWithLayer) => this.resolveFeatureEntityId(feature));
    }

    private resolveFeatureEntityId(feature: MapGeoJSONFeatureWithLayer): string {
        const propertyKey =
            this.isFieldInterventionSelectionEnabled() && this.entitiesTableByLayerService.isPlanningSelectionActive()
                ? 'entityId'
                : 'uuid';

        const entityId = feature.properties[propertyKey];
        AssertService.isString(entityId);

        return entityId;
    }

    private resolveFieldInterventionListService(): FieldInterventionMapSelectionHandler {
        return this.createFieldInterventionService.isCreateFieldIntervention() ||
            this.fieldInterventionChangeSelectionService.isActive()
            ? this.createFieldInterventionService
            : this.entitiesTableByLayerService;
    }

    private resolveStyleTypeFromEntity(
        entity: LayerControllerGetEntities200ResponseAllOfItemsInner,
    ): LayerStyleTypeEnum | undefined {
        if (!entity.geom) {
            return undefined;
        }

        AssertService.assertGeometryType(entity.geom);

        return this.mapSdkGeometryToStyleType(entity.geom);
    }

    private restoreIsSelectionActiveFromSessionStorage(): void {
        const storedValue = sessionStorage.getItem(SESSION_STORAGE_SELECTION_ACTIVE_KEY);

        if (storedValue === null) {
            return;
        }

        this.isSelectionActive.set(storedValue === 'true');
    }

    private synchronizeToSessionStorage(isSelectionActive: boolean): void {
        sessionStorage.setItem(SESSION_STORAGE_SELECTION_ACTIVE_KEY, String(isSelectionActive));

        this.layerBroadcastChannel.postMessage({
            additionalData: isSelectionActive,
            message: BroadcastChannelMessageEnum.SELECTION_ACTIVE,
            source: BroadcastChannelSourceEnum.TABLE_VIEW,
        });
    }

    private toggleFeatureSelection(feature: MapGeoJSONFeatureWithLayer): void {
        if (this.isFieldInterventionSelectionEnabled()) {
            this.resolveFieldInterventionListService().toggleEntitiesFromMapFeatures([feature]);

            return;
        }

        const layerId: string = feature.source;
        const selectedEntity: SelectedEntity = this.mapFeatureToSelectedEntity(feature);

        this.selectionStateService.updateSelectedEntitiesByLayerId(
            (previous: SelectedEntitiesByLayerId): SelectedEntitiesByLayerId => {
                return this.buildEntitiesByLayerId(previous, layerId, selectedEntity);
            },
        );
    }
}
